import {
  AccredibleProfileAvailability,
  CustomAttribute,
  CustomAttributeType,
} from '@accredible-frontend-v2/models';
import { Params } from '@angular/router';
import { SpotlightProfilesFilters } from '../../core/models/spotlight-profiles.model';

export const buildSpotlightFilters = (
  queryParams: Params,
  currentFilters: SpotlightProfilesFilters,
  searchableCustomAttributes: CustomAttribute[],
): SpotlightProfilesFilters => {
  const {
    page = 1,
    name,
    search_term,
    search_by,
    order = '',
    groups,
    organizations,
    location,
    city,
    custom_location_distance: queryParamRadius,
    country,
    state,
    skills,
    categories,
    availability,
  } = queryParams;

  const { custom_location_distance: filterRadius, lat, lng, viewport } = currentFilters || {};

  return {
    page,
    page_size: 20,
    search_term,
    search_by,
    order,
    groups: buildArrayFilter(groups),
    organizations: buildArrayFilter(organizations),
    location,
    lat: location ? lat : null,
    lng: location ? lng : null,
    city,
    custom_location_distance: queryParamRadius || filterRadius,
    country,
    state,
    viewport: location ? viewport : null,
    skills: buildFilter<string>(skills),
    skill_category_ids: buildFilter(categories).map(Number),
    availability: buildFilter<AccredibleProfileAvailability>(availability),
    custom_attributes: addQueryParamValuesToCustomAttributes(
      queryParams,
      searchableCustomAttributes,
    ),
  };
};

export const addQueryParamValuesToCustomAttributes = (
  queryParams: Params,
  searchableCustomAttributes: CustomAttribute[],
): CustomAttribute[] => {
  // Copy the searchableCustomAttributes array but add any values from the query params to the custom attributes and return the custom attributes
  return searchableCustomAttributes.map((customAttribute: CustomAttribute) => {
    const paramValue = queryParams[customAttribute.id];
    const customAttributeWithValue = { ...customAttribute, value: null };

    if (!paramValue) {
      return customAttributeWithValue;
    }

    // If a query param value is present for a valid id use it add it to the custom attribute
    switch (customAttribute.type) {
      case CustomAttributeType.SELECT_ANY:
        // If param value is an array
        if (Array.isArray(paramValue)) {
          // Map it to an array of numbers and remove any non-number values
          customAttributeWithValue.value = mapToArrayOfNumbers(paramValue) || null;
        } else {
          if (!isNaN(paramValue)) {
            // If the param is a number
            customAttributeWithValue.value = [Number(paramValue)];
          }
        }
        break;

      case CustomAttributeType.SELECT_ONE:
        if (Array.isArray(paramValue)) {
          // Map it to an array of numbers and remove any non-number values we only want one number so take the first
          customAttributeWithValue.value = mapToArrayOfNumbers(paramValue)[0] || null;
        }
        if (!isNaN(paramValue)) {
          // If the param is a number
          customAttributeWithValue.value = Number(paramValue);
        }
        break;

      case CustomAttributeType.BOOLEAN:
        if (Array.isArray(paramValue)) {
          customAttributeWithValue.value = extractFirstBooleanValue(paramValue);
        } else {
          if (isBoolean(paramValue)) {
            customAttributeWithValue.value = stringToBoolean(paramValue);
          }
        }
        break;

      default:
        // CustomAttributeType.TEXT
        if (Array.isArray(paramValue)) {
          // If the paramValue is an array take the first value, decode it and set it to the custom attribute value
          customAttributeWithValue.value = decodeURI(paramValue[0]);
        } else {
          // Otherwise, just decode the value and set it to the custom attribute value
          customAttributeWithValue.value = decodeURI(paramValue);
        }
        break;
    }

    return customAttributeWithValue;
  });
};

/**
 * Because query params are always read as strings we need to convert them into numbers.
 * Also, if we have more than one item, the query param is an Array, else it's a string,
 * so we need to convert into an Array.
 */
export const buildArrayFilter = (items: string | string[]): number[] => {
  if (Array.isArray(items)) {
    return items.map((item) => Number(item));
  }
  if (items) {
    return [Number(items)];
  }
  return [];
};

export const buildFilter = <T>(filter: T | T[]): T[] => {
  if (Array.isArray(filter)) {
    return filter;
  }
  if (filter) {
    return [filter];
  }
  return [];
};

/*
  Map an array to an array of numbers and remove any values that are not numbers
 */
export const mapToArrayOfNumbers = (array: any[]): number[] => {
  return array.reduce((arr, el) => {
    if (el && !isNaN(el)) {
      arr.push(Number(el));
    }
    return arr;
  }, []);
};

/*
  Extract the first valid boolean value from an array of unknown values
 */
export const extractFirstBooleanValue = (array: any[]): boolean => {
  array.forEach((value) => {
    if (isBoolean(value)) {
      return stringToBoolean(value);
    }
  });
  return null;
};

/*
  Receives a string or boolean and returns true if value is a boolean
 */
export const isBoolean = (val: string | boolean): boolean => {
  if (typeof val === 'boolean') {
    return true;
  } else if (typeof val === 'string') {
    return val.toLowerCase() === 'true' || val.toLowerCase() === 'false';
  }
  return false;
};

/*
  Receives a string and returns the boolean value or null if the value is not a boolean
 */
export const stringToBoolean = (val: string): boolean => {
  if (val.toLowerCase() === 'true') {
    return true;
  }
  if (val.toLowerCase() === 'false') {
    return false;
  }
  return null;
};
